import { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Remove static HTML hero shell once React mounts
const shell = document.getElementById("static-hero");
if (shell) {
  shell.style.opacity = "0";
  setTimeout(() => shell.remove(), 300);
}

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Lazy load pages for better performance
const HomePage = lazy(() => import("@/pages/HomePage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ServicesPage = lazy(() => import("@/pages/ServicesPage"));
const PortfolioPage = lazy(() => import("@/pages/PortfolioPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const BookingPage = lazy(() => import("@/pages/BookingPage"));
const LocationPage = lazy(() => import("@/pages/LocationPage"));
const PolicyPage = lazy(() => import("@/pages/PolicyPage"));
const BridalServicePage = lazy(() => import("@/pages/BridalServicePage"));
const PartyServicePage = lazy(() => import("@/pages/PartyServicePage"));
const MaternityServicePage = lazy(() => import("@/pages/MaternityServicePage"));
const EditorialServicePage = lazy(() => import("@/pages/EditorialServicePage"));

// Admin pages
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminBlogs = lazy(() => import("@/pages/admin/AdminBlogs"));
const AdminPortfolio = lazy(() => import("@/pages/admin/AdminPortfolio"));
const AdminPolicies = lazy(() => import("@/pages/admin/AdminPolicies"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-nude-cream">
    <div className="text-center">
      <div className="w-12 h-12 border-2 border-nude-rose border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-nude-grey font-body">Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/bridal-hair-makeup" element={<BridalServicePage />} />
            <Route path="/services/party-event-makeup" element={<PartyServicePage />} />
            <Route path="/services/maternity-makeup" element={<MaternityServicePage />} />
            <Route path="/services/editorial-makeup" element={<EditorialServicePage />} />
            <Route path="/portfolio" element={<PortfolioPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/booking" element={<BookingPage />} />
            <Route path="/location/:city" element={<LocationPage />} />
            <Route path="/privacy-policy" element={<PolicyPage />} />
            <Route path="/terms-of-service" element={<PolicyPage />} />
            <Route path="/cancellation-policy" element={<PolicyPage />} />
            <Route path="/refund-policy" element={<PolicyPage />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />}>
              <Route index element={<Navigate to="/admin/blogs" replace />} />
              <Route path="blogs" element={<AdminBlogs />} />
              <Route path="portfolio" element={<AdminPortfolio />} />
              <Route path="policies" element={<AdminPolicies />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>
          </Routes>
        </Suspense>
        <Toaster position="bottom-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
